import BN from "bignumber.js";
import ApiRequest from "@/common/lib/apiRequest";
import StakeApiRequest from "@/common/lib/stakeApiRequest";
import Config from "@/common/config";
import anchor from "./anchor.module";
import osmosis from "./osmosis.module";

const mutationTypes = {
  SET_TX_ACTION: "tendermint/SET_TX_ACTION",
  SET_ESTIMATED_APR: "tendermint/SET_ESTIMATED_APR",
  SET_BLOCK_INFO: "tendermint/SET_BLOCK_INFO",
  SET_TOTAL_STAKED: "tendermint/SET_TOTAL_STAKED",
  SET_ACCOUNT_INFO: "tendermint/SET_ACCOUNT_INFO",
  SET_ASSET_PRICES: "tendermint/SET_ASSET_PRICES",
  SET_STAKED_SWU_BALANCES: "tendermint/SET_STAKED_SWU_BALANCES",
  SET_ALL_STAKED_BALANCES: "tendermint/SET_ALL_STAKED_BALANCES",
  SET_UNBONDING_BALANCES: "tendermint/SET_UNBONDING_BALANCES",
  SET_REWARDS_BALANCES: "tendermint/SET_REWARDS_BALANCES",
  SET_PROPOSALS: "tendermint/SET_PROPOSALS",
  UPDATE_PROPOSAL: "tendermint/UPDATE_PROPOSAL",
  SET_PROPOSAL_PARAMETERS: "tendermint/SET_PROPOSAL_PARAMETERS",
  SET_VOTING_PROPOSAL: "tendermint/SET_VOTING_PROPOSAL",
  SET_GRANTS: "tendermint/SET_GRANTS",
  RESET_STATE: "tendermint/RESET_STATE",
};

const getDefaultState = () => ({
  currentTxAction: "",
  estimatedApr: {
    fetching: false,
    result: null,
    error: null,
  },
  blockInfo: {
    fetching: false,
    result: null,
    error: null,
  },
  totalStaked: {
    fetching: false,
    result: null,
    error: null,
  },
  assetPrices: {
    fetching: false,
    result: [],
    error: null,
  },
  accountInfo: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedWithUs: {
    fetching: false,
    result: null,
    error: null,
  },
  allStaked: {
    fetching: false,
    result: null,
    error: null,
  },
  unbonding: {
    fetching: false,
    result: null,
    error: null,
  },
  rewards: {
    fetching: false,
    result: null,
    error: null,
  },
  proposals: {
    fetching: false,
    result: null,
    error: null,
  },
  proposalParameters: {
    fetching: false,
    result: null,
    error: null,
  },
  votingProposal: null,
  grants: {
    fetching: false,
    result: null,
    error: null,
  },
});

const state = getDefaultState();

const actions = {
  setTxAction({ commit }, action) {
    commit(mutationTypes.SET_TX_ACTION, action);
  },
  async getEstimatedApr({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    commit(mutationTypes.SET_ESTIMATED_APR, {
      fetching: true,
    });
    try {
      let result;
      if (project.name === "starname") {
        result = {
          value: 0.32,
          note: `Based off <a class="black--text" href="https://medium.com/iov-internet-of-values/what-apy-will-the-starname-iov-blockchain-have-when-inflation-is-0-5d623c6f0a27" target="_blank" rel="noopener noreferrer">estimates</a> from IOV team`,
        };
      } else {
        result = await StakeApiRequest.get(
          `/yield/${project.name}/${project.staking.chainId}`
        );
      }
      commit(mutationTypes.SET_ESTIMATED_APR, {
        fetching: false,
        result: {
          value: result.value * 100,
          note: result.note,
          compoundApy: result.compoundApy * 100,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_ESTIMATED_APR, {
        fetching: false,
        error,
      });
    }
  },
  async getBlockInfo({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    commit(mutationTypes.SET_BLOCK_INFO, {
      fetching: true,
    });
    try {
      const { value: result } = await StakeApiRequest.get(
        `/network-info/${project.name}/${project.staking.chainId}`
      );
      commit(mutationTypes.SET_BLOCK_INFO, {
        fetching: false,
        result: {
          chainId: project.staking?.chainId,
          ...result,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_BLOCK_INFO, {
        fetching: false,
        error,
      });
    }
  },
  setBlockInfo({ commit, state }, { blockTime, blockHeight }) {
    commit(mutationTypes.SET_BLOCK_INFO, {
      fetching: false,
      result: {
        ...state.blockInfo.result,
        blockTime,
        blockHeight,
      },
    });
  },
  async getTotalStaked({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    commit(mutationTypes.SET_TOTAL_STAKED, {
      fetching: true,
    });
    try {
      const result = await StakeApiRequest.get(
        `/total-staked/${project.name}/${project.staking.chainId}`
      );
      commit(mutationTypes.SET_TOTAL_STAKED, {
        fetching: false,
        result,
      });
    } catch (error) {
      commit(mutationTypes.SET_TOTAL_STAKED, {
        fetching: false,
        error,
      });
    }
  },
  async fetchAssetPriceChart(
    { commit, rootGetters },
    { startTime, type = "h" } = {}
  ) {
    const project = rootGetters["projectConfig/latestProject"];
    commit(mutationTypes.SET_ASSET_PRICES, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get(
        `/${project.name}/network/prices/${startTime}`,
        { params: { type } }
      );
      const result = getAssetPrices(data, type);
      commit(mutationTypes.SET_ASSET_PRICES, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_ASSET_PRICES, { error, fetching: false });
    }
  },
  async getAccountBalances({ commit, state, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const accountInfo = state.accountInfo;
    try {
      const { balances } = await StakeApiRequest.get(
        `/balance/${project.name}/${project.staking.chainId}/${wallet.address}`
      );
      const result = accountInfo.result;
      const total =
        balances.find(({ denom }) => denom === project.staking.denom)?.value ??
        0;
      commit(mutationTypes.SET_ACCOUNT_INFO, {
        result: {
          ...result,
          balances,
          total,
        },
      });
    } catch (error) {}
  },
  async getAccountInfo({ commit, rootGetters, state }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    if (!project || !wallet?.address) return;
    const accountInfo = state.accountInfo;
    const cResult = accountInfo.result || {};
    commit(mutationTypes.SET_ACCOUNT_INFO, {
      fetching: true,
    });
    try {
      const result = await StakeApiRequest.get(
        `/account/${project.name}/${project.staking.chainId}/${wallet.address}`
      );
      delete result.balances;
      result.accountExisted = true;
      commit(mutationTypes.SET_ACCOUNT_INFO, {
        fetching: false,
        result: {
          ...cResult,
          ...result,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_ACCOUNT_INFO, {
        fetching: false,
        result: {
          balances: [],
          total: 0,
          accountExisted: false,
        },
      });
    }
  },
  async getStakedSwuBalances({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_STAKED_SWU_BALANCES, { fetching: true });
    try {
      const result = await StakeApiRequest.get(
        `/delegators/swu/${project.name}/${project.staking.chainId}/${wallet.address}`
      );
      commit(mutationTypes.SET_STAKED_SWU_BALANCES, {
        fetching: false,
        result: result || {
          balance: 0,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_SWU_BALANCES, {
        fetching: false,
        result: {
          balance: 0,
        },
      });
    }
  },
  async getAllStakedBalances({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_ALL_STAKED_BALANCES, { fetching: true });
    try {
      const result = await StakeApiRequest.get(
        `/delegators/validators/${project.name}/${project.staking.chainId}/${wallet.address}`
      );
      const total = result.reduce(
        (sum, { balance: value }) => new BN(sum).plus(value).valueOf(),
        0
      );
      commit(mutationTypes.SET_ALL_STAKED_BALANCES, {
        fetching: false,
        result: {
          balances: result,
          total,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_ALL_STAKED_BALANCES, {
        fetching: false,
        result: {
          balances: [],
          total: 0,
        },
      });
    }
  },
  async getUnbondingBalances({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_UNBONDING_BALANCES, { fetching: true });
    try {
      let result = await StakeApiRequest.get(
        `/delegators/unbonding-delegations/list/${project.name}/${project.staking.chainId}/${wallet.address}`
      );
      result = result.map(item => ({
        denom: item.denom,
        balance: item.entries[0].balance,
        endsOn: item.entries[0].completion_time,
        validator: item.validator || {},
        validatorAddr: item.validator_address,
      }));
      const total = result.reduce(
        (sum, { balance: value }) => new BN(sum).plus(value).valueOf(),
        0
      );
      commit(mutationTypes.SET_UNBONDING_BALANCES, {
        fetching: false,
        result: {
          balances: result,
          total,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_UNBONDING_BALANCES, {
        fetching: false,
        result: {
          balances: [],
          total: 0,
        },
      });
    }
  },
  async getRewardBalances({ commit, rootGetters, dispatch }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_REWARDS_BALANCES, { fetching: true });
    try {
      let { rewards } = await StakeApiRequest.get(
        `/unclaimed-rewards/list/${project.name}/${project.staking.chainId}/${wallet.address}`
      );
      rewards = rewards
        .filter(item => !!item)
        .map(item => ({
          denom: item.denom,
          balance: item.value,
          validator: item.validator || {},
          validatorAddr: item.validator_address,
          reward: item.reward,
        }));
      let total = rewards
        .filter(item => item?.denom === project.staking.denom)
        .reduce(
          (sum, { balance: value }) => new BN(sum).plus(value).valueOf(),
          0
        );
      const allDenomRewards = getAllDenomRewards(rewards);
      if (project.staking.swapableCoins?.length) {
        const swaprates = await dispatch(
          "fetchSwaprate",
          project.staking.denom
        );
        total = new BN(total)
          .plus(getDenomValue(allDenomRewards, swaprates))
          .valueOf();
        rewards = rewards.map(rw => ({
          ...rw,
          balance: new BN(rw.balance)
            .plus(
              getDenomValue(
                rw.reward.reduce(
                  (acc, cur) => ({ ...acc, [cur.denom]: cur.amount }),
                  {}
                ),
                swaprates
              )
            )
            .valueOf(),
        }));
      }
      commit(mutationTypes.SET_REWARDS_BALANCES, {
        fetching: false,
        result: {
          balances: rewards,
          allDenomRewards,
          total,
        },
      });
    } catch (error) {
      commit(mutationTypes.SET_REWARDS_BALANCES, {
        fetching: false,
        result: {
          balances: [],
          allDenomRewards: [],
          total: 0,
        },
      });
    }
  },
  async fetchProposals({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_PROPOSALS, { fetching: true, result: [] });
    try {
      let result = await StakeApiRequest.get(
        `/proposal/${project.name}/${project.staking.chainId}/list`,
        {
          params: {
            address: wallet?.address,
          },
          timeout: 60000,
        }
      );
      result = result.sort((a, b) => b.id - a.id);
      commit(mutationTypes.SET_PROPOSALS, { fetching: false, result });
    } catch (error) {
      commit(mutationTypes.SET_PROPOSALS, { fetching: false, error });
    }
  },
  async fetchProposalInfo({ rootGetters }, proposalId) {
    const project = rootGetters["projectConfig/latestProject"];
    try {
      let result = await StakeApiRequest.get(
        `/proposal/${project.name}/${project.staking.chainId}/id/${proposalId}`,
        {
          timeout: 60000,
        }
      );
      return result;
    } catch (error) {
      return null;
    }
  },
  async fetchProposalTally({ rootGetters }, proposalId) {
    const project = rootGetters["projectConfig/latestProject"];
    const result = await StakeApiRequest.get(
      `/proposal/${project.name}/${project.staking.chainId}/id/${proposalId}/tally`,
      {
        timeout: 60000,
      }
    );
    return result;
  },
  async fetchProposalParameters({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    try {
      let result = await StakeApiRequest.get(
        `/proposal/${project.name}/${project.staking.chainId}/parameters`
      );
      return result;
    } catch (error) {
      return null;
    }
  },
  async fetchProposer({ rootGetters, state, commit }, proposalId) {
    const project = rootGetters["projectConfig/latestProject"];
    const proposals = state.proposals.result || [];
    const pidx = proposals.findIndex(p => p.id === proposalId);
    if (proposals[pidx]?.proposer) return proposals[pidx]?.proposer;
    const proposer = await StakeApiRequest.get(
      `/proposal/${project.name}/${project.staking.chainId}/id/${proposalId}/proposer`
    );
    return proposer;
  },
  async getStakeRawTx(
    { rootGetters, getters, dispatch },
    { amount, memo, gas, gasPrice, shouldWithdrawRewards }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    if (!getters.accountInfo.result || !getters.blockInfo.result) return null;
    const rewards = getters.rewards.result?.balances ?? [];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { validatorAddr, chainId } = getters.blockInfo.result;
    return stakeUtils.txBuilder.getRawTx("stake", {
      shouldWithdrawRewards,
      rewards,
      amount: new BN(amount)
        .multipliedBy(project.staking.conversionRate)
        .toFixed(0, BN.ROUND_DOWN),
      denom: project.staking.denom,
      delegatorAddr: wallet.address,
      validatorAddr,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getUnstakeRawTx(
    { rootGetters, getters, dispatch },
    { amount, memo, validatorAddr, gas, gasPrice }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;
    return stakeUtils.txBuilder.getRawTx("unstake", {
      amount: new BN(amount)
        .multipliedBy(project.staking.conversionRate)
        .toFixed(0, BN.ROUND_DOWN),
      denom: project.staking.denom,
      delegatorAddr: wallet.address,
      validatorAddr,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getRedelegateRawTx(
    { rootGetters, getters, dispatch },
    { amount, memo, validatorAddrSrc, gas, gasPrice }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId, validatorAddr } = getters.blockInfo.result;

    return stakeUtils.txBuilder.getRawTx("redelegate", {
      amount: new BN(amount)
        .multipliedBy(project.staking.conversionRate)
        .toFixed(0, BN.ROUND_DOWN),
      denom: project.staking.denom,
      delegatorAddr: wallet.address,
      validatorAddrSrc,
      validatorAddrDst: validatorAddr,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getClaimRewardsRawTx(
    { rootGetters, getters, dispatch },
    { memo, amount, shouldStake, validators, gas, gasPrice }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId, validatorAddr } = getters.blockInfo.result;

    return stakeUtils.txBuilder.getRawTx("claim", {
      shouldStake,
      validators,
      validatorAddr,
      amount: new BN(amount)
        .multipliedBy(project.staking.conversionRate)
        .toFixed(0, BN.ROUND_DOWN),
      denom: project.staking.denom,
      delegatorAddr: wallet.address,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getSendRawTx(
    { rootGetters, getters, dispatch },
    { amount, memo, toAddress, gasPriceStep }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;
    gasPriceStep = gasPriceStep || project.staking.gasPriceStep.medium;
    return stakeUtils.txBuilder.getRawTx("send", {
      amount: new BN(amount)
        .multipliedBy(project.staking.conversionRate)
        .toFixed(0, BN.ROUND_DOWN),
      denom: project.staking.denom,
      fromAddress: wallet.address,
      toAddress,
      chainId,
      gasPrice: new BN(project.staking.gas.Send)
        .multipliedBy(gasPriceStep)
        .toNumber(),
      gas: project.staking.gas.Send,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getSwapCoinRawTx(
    { rootGetters, getters, dispatch },
    { amount, memo, offerDenom, askDenom, feeDenom, gas, gasPriceStep }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;
    gasPriceStep = gasPriceStep || project.staking.gasPriceStep.medium;
    return stakeUtils.txBuilder.getRawTx("swap", {
      amount,
      feeDenom,
      askDenom,
      offerDenom,
      fromAddress: wallet.address,
      chainId,
      gas,
      gasPrice: new BN(gas).multipliedBy(gasPriceStep).toFixed(0),
      memo,
      accountNumber,
      sequence,
    });
  },
  async getSwapCoinEstimatedGas(
    { rootGetters, getters },
    { amount, askDenom, offerDenom }
  ) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;
    return fetch(`https://fcd.terra.dev/market/swap`, {
      method: "POST",
      body: JSON.stringify({
        base_req: {
          from: wallet.address,
          chain_id: chainId,
          account_number: accountNumber,
          sequence,
          simulate: true,
          gas: "auto",
          fees: [{ amount: "0", denom: project.staking.denom }],
        },
        ask_denom: askDenom,
        offer_coin: { amount: String(amount), denom: offerDenom },
      }),
    })
      .then(res => res.json())
      .then(res => res.gas_estimate);
  },
  async getVoteRawTx(
    { rootGetters, getters, dispatch },
    { memo, proposalId, proposalOption, gas, gasPrice }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;

    return stakeUtils.txBuilder.getRawTx("vote", {
      proposalId,
      proposalOption,
      denom: project.staking.denom,
      voter: wallet.address,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getGovDepositRawTx(
    { rootGetters, getters, dispatch },
    { memo, proposalId, amount, gas, gasPrice }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/walletSelected"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;

    return stakeUtils.txBuilder.getRawTx("deposit", {
      proposalId,
      amount: new BN(amount)
        .multipliedBy(project.staking.conversionRate)
        .toFixed(0, BN.ROUND_DOWN),
      denom: project.staking.denom,
      voter: wallet.address,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getEnableAutoCompoundRawTx(
    { rootGetters, getters, dispatch },
    { botAddr, validatorAddr, expiration, gas, gasPrice, memo }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;

    return stakeUtils.txBuilder.getRawTx("enableAutoCompound", {
      granter: wallet.address,
      grantee: botAddr,
      validatorAddr,
      expiration,
      feeDenom: project.staking.feeDenom || project.staking.denom,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async getDisableAutoCompoundRawTx(
    { rootGetters, getters, dispatch },
    { botAddr, gas, gasPrice, memo, grants }
  ) {
    await dispatch("getAccountInfo");
    const stakeUtils = rootGetters["stakeUtils/utils"];
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    const { accountNumber, sequence } = getters.accountInfo.result;
    const { chainId } = getters.blockInfo.result;
    return stakeUtils.txBuilder.getRawTx("disableAutoCompound", {
      granter: wallet.address,
      grantee: botAddr,
      grants,
      feeDenom: project.staking.feeDenom || project.staking.denom,
      chainId,
      gasPrice,
      gas,
      memo,
      accountNumber,
      sequence,
    });
  },
  async broadcastTx({ rootGetters }, { signature, rawTx, metadata, tx_bytes }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    let path = "broadcast-direct";
    let payload = {
      metadata,
      tx_bytes,
    };
    if (!payload.tx_bytes) {
      payload = {
        signatures: [signature],
        accountNumber: rawTx.account_number,
        sequence: rawTx.sequence,
        fee: rawTx.fee,
        msg: rawTx.msgs,
        memo: rawTx.memo,
        metadata,
      };
      path = "broadcast";
    }
    return StakeApiRequest.post(
      `/${path}/${project.name}/${project.staking.chainId}/${wallet.address}`,
      payload,
      {
        headers: {
          [Config.requestHeaders.authToken]: rootGetters["auth/accessToken"],
        },
      }
    );
  },
  async createTx({ rootGetters }, { txHash, metadata }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    return StakeApiRequest.post(
      `/transaction/create/${project.name}/${project.staking.chainId}/${wallet.address}`,
      {
        txHash,
        metadata,
      },
      {
        headers: {
          [Config.requestHeaders.authToken]: rootGetters["auth/accessToken"],
        },
      }
    );
  },
  async simulateTx({ rootGetters, dispatch }, { rawTx, pubkey }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    if (!rawTx || !rawTx.sequence)
      return { error: "Tx is null", rawTxUpdated: { fee: { gas: 0 } } };
    const payload = {
      pubkey,
      sequence: rawTx.sequence,
      msgs: await dispatch("walletUtils/convertTendermintMsgs", rawTx.msgs, {
        root: true,
      }),
    };
    const result = await StakeApiRequest.post(
      `/simulate/${project.name}/${project.staking.chainId}/${wallet.address}`,
      payload,
      {
        headers: {
          [Config.requestHeaders.authToken]: rootGetters["auth/accessToken"],
        },
      }
    );
    const gasPriceStep = new BN(rawTx.fee.amount[0].amount).div(rawTx.fee.gas);
    const newGas = result.gasLimit || rawTx.fee.gas;
    const newFee = {
      amount: [
        {
          amount: gasPriceStep.multipliedBy(newGas).toFixed(0, BN.ROUND_CEIL),
          denom: rawTx.fee.amount[0].denom,
        },
      ],
      gas: newGas,
    };
    rawTx.fee = newFee;
    return {
      shouldBroadcast: result.shouldBroadcast,
      error: result.error,
      rawTxUpdated: rawTx,
    };
  },
  async fetchSwaprate({}, denom) {
    return fetch(
      `https://fcd.terra.dev/v1/market/swaprate/${denom}`
    ).then(res => res.json());
  },
  async fetchSwapEstimatedAmount({}, { offerDenom, amount, askDenom }) {
    return fetch(
      `https://fcd.terra.dev/market/swap?offer_coin=${amount}${offerDenom}&ask_denom=${askDenom}`
    )
      .then(res => res.json())
      .then(res => res.result);
  },
  async fetchNetworkInfo({ dispatch }) {
    return Promise.all([
      dispatch("getEstimatedApr"),
      dispatch("getBlockInfo"),
      dispatch("getTotalStaked"),
      dispatch("fetchAssetPriceChart"),
    ]);
  },
  async fetchRequiredInfo({ dispatch, getters, rootGetters }) {
    const signableWallet = rootGetters["userWallets/walletSelected"];
    if (!signableWallet || signableWallet.coin_network === "ethereum") return;
    await dispatch("getAccountInfo");
    if (!getters.accountInfo.result?.accountExisted) {
      return dispatch("resetState", true);
    }
    return Promise.all([
      dispatch("getAccountBalances"),
      dispatch("getStakedSwuBalances"),
      dispatch("getAllStakedBalances"),
      dispatch("getUnbondingBalances"),
      dispatch("getRewardBalances"),
      dispatch("fetchGrants"),
    ]);
  },
  async fetchProposalsInfo({ dispatch }) {
    return Promise.all([
      dispatch("fetchProposals"),
      dispatch("fetchProposalParameters"),
    ]);
  },
  async fetchGrants({ commit, rootGetters }) {
    const project = rootGetters["projectConfig/latestProject"];
    const wallet = rootGetters["userWallets/signableWallet"];
    commit(mutationTypes.SET_GRANTS, { fetching: true });
    try {
      const result = await StakeApiRequest.get(
        `/grants/${project.name}/${wallet.address}`
      );
      commit(mutationTypes.SET_GRANTS, {
        fetching: false,
        result,
      });
    } catch (error) {
      commit(mutationTypes.SET_GRANTS, {
        fetching: false,
      });
    }
  },
  resetState({ commit }, keepOverview) {
    commit(
      mutationTypes.RESET_STATE,
      keepOverview
        ? ["estimatedApr", "blockInfo", "assetPrices", "totalStaked"]
        : []
    );
  },
};

const mutations = {
  [mutationTypes.SET_TX_ACTION](state, action) {
    state.currentTxAction = action;
  },
  [mutationTypes.SET_ACCOUNT_INFO](state, data) {
    state.accountInfo = {
      ...state.accountInfo,
      ...data,
    };
  },
  [mutationTypes.SET_ESTIMATED_APR](state, data) {
    state.estimatedApr = {
      ...state.estimatedApr,
      ...data,
    };
  },
  [mutationTypes.SET_BLOCK_INFO](state, data) {
    state.blockInfo = {
      ...state.blockInfo,
      ...data,
    };
  },
  [mutationTypes.SET_TOTAL_STAKED](state, data) {
    state.totalStaked = {
      ...state.totalStaked,
      ...data,
    };
  },
  [mutationTypes.SET_ASSET_PRICES](state, data) {
    state.assetPrices = {
      ...state.assetPrices,
      ...data,
    };
  },
  [mutationTypes.SET_STAKED_SWU_BALANCES](state, data) {
    state.stakedWithUs = {
      ...state.stakedWithUs,
      ...data,
    };
  },
  [mutationTypes.SET_ALL_STAKED_BALANCES](state, data) {
    state.allStaked = {
      ...state.allStaked,
      ...data,
    };
  },
  [mutationTypes.SET_UNBONDING_BALANCES](state, data) {
    state.unbonding = {
      ...state.unbonding,
      ...data,
    };
  },
  [mutationTypes.SET_REWARDS_BALANCES](state, data) {
    state.rewards = {
      ...state.rewards,
      ...data,
    };
  },
  [mutationTypes.SET_PROPOSALS](state, data) {
    state.proposals = {
      ...state.proposals,
      ...data,
    };
  },
  [mutationTypes.UPDATE_PROPOSAL](
    state,
    { index, proposer, final_tally_result }
  ) {
    state.proposals.result[index].proposer =
      proposer || state.proposals.result[index].proposer;
    state.proposals.result[index].final_tally_result =
      final_tally_result || state.proposals.result[index].final_tally_result;
  },
  [mutationTypes.SET_PROPOSAL_PARAMETERS](state, data) {
    state.proposalParameters = {
      ...state.proposalParameters,
      ...data,
    };
  },
  [mutationTypes.SET_VOTING_PROPOSAL](state, data) {
    state.votingProposal = {
      ...state.votingProposal,
      ...data,
    };
  },
  [mutationTypes.SET_GRANTS](state, data) {
    state.grants = {
      ...state.grants,
      ...data,
    };
  },
  [mutationTypes.RESET_STATE](state, excludedFields = []) {
    const newState = getDefaultState();
    for (const key in newState) {
      if (!excludedFields.includes(key)) {
        state[key] = newState[key];
      }
    }
  },
};

const getters = {
  currentTxAction: state => state.currentTxAction,
  accountInfo: state => state.accountInfo,
  estimatedApr: state => state.estimatedApr,
  blockInfo: state => state.blockInfo,
  totalStaked: state => state.totalStaked,
  assetPrices: state => state.assetPrices,
  stakedWithUs: state => state.stakedWithUs,
  allStaked: state => state.allStaked,
  unbonding: state => state.unbonding,
  rewards: state => state.rewards,
  proposals: state => state.proposals,
  proposalParameters: state => state.proposalParameters,
  votingProposal: state => state.votingProposal,
  grants: state => state.grants,
  totalAssets: state => {
    return new BN(state.allStaked.result?.total || 0)
      .plus(state.unbonding.result?.total || 0)
      .plus(state.rewards.result?.total || 0)
      .plus(state.accountInfo.result?.total || 0)
      .valueOf();
  },
};

const getAssetPrices = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  if (data.length === 1) {
    data = [data[0], { ...data[0], timestamp: new Date() }];
  }
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  return result;
};

const getAllDenomRewards = rewards => {
  const result = {};
  rewards.forEach(({ reward }) => {
    reward.forEach(({ denom, amount }) => {
      result[denom] = new BN(result[denom] || 0).plus(amount).toNumber();
    });
  });
  return result;
};

const getDenomValue = (denomRewards, swaprates) => {
  if (!Object.keys(denomRewards).length) return 0;
  return swaprates.reduce(
    (acc, cur) =>
      new BN(acc)
        .plus(new BN(denomRewards[cur.denom] || 0).dividedBy(cur.swaprate))
        .toNumber(),
    0
  );
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  modules: {
    anchor,
    osmosis,
  },
};
